import { useRef } from "react";
import { Form, Spinner } from "react-bootstrap";
import BankStatementsUploadForm from "../bank_statement_upload_form";
import MarketplaceStatementsUploadForm from "../marketplace_statements_upload_form";
import FinancialStatementsUploadForm from "../financial_statements_upload_form";
import BuyerInvoicesUploadForm from "../buyer_invoices_upload_form";
import DocumentFiles from "../document_files";

const DocumentUploadForm = ({
  showLoader = false,
  documentTypes,
  documentsList,
  onChange,
  setFiles,
  reloadDocumentsList,
  errors = {},
  vatReturnsRequired = false,
  uploading = false,
  onUploadingChange,
  isRequired,
}) => {
  const inputRefs = useRef({});

  const docRequired = (doc) =>
    isRequired
      ? isRequired(doc)
      : doc.is_mandatory || (doc.document_type_key === "vat_returns" && vatReturnsRequired);

  const handleFileChange = (docTypeKey, e) => {
    if (onChange) onChange(docTypeKey, e, afterUpload);
    if (setFiles) {
      const selectedFiles = Array.from(e.target.files);
      setFiles((prev) => ({
        ...prev,
        [docTypeKey]: selectedFiles,
      }));
    }
  };

  const afterUpload = (docTypeKey) => {
    if (inputRefs.current[docTypeKey]) {
      inputRefs.current[docTypeKey].value = "";
    }
  };

  return (
    <div className="position-relative">
      {uploading && (
        <>
          <div
            className="d-flex justify-content-center mb-3"
            role="status"
            aria-live="polite"
            style={{
              position: "sticky",
              top: 8,
              zIndex: 20,
            }}
          >
            <div
              className="d-flex align-items-center bg-white border shadow-sm px-4 py-2"
              style={{ borderRadius: 999 }}
            >
              <Spinner animation="border" size="sm" />
              <span className="ms-2">Uploading file...</span>
            </div>
          </div>
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(255, 255, 255, 0.6)",
              zIndex: 4,
              borderRadius: 8,
            }}
          />
        </>
      )}
      {showLoader ? (
        <Spinner animation="border" />
      ) : (
        <>
          {documentTypes.map((doc) => (
            <div key={doc.id} id={`doc-section-${doc.document_type_key}`} tabIndex={-1}>
              {doc.document_type_key === "bank_statements" ? (
                <BankStatementsUploadForm
                  doc={doc}
                  onChange={onChange}
                  reloadDocumentsList={reloadDocumentsList}
                  documentsList={documentsList}
                  validationError={errors?.[doc.document_type_key]}
                  onUploadingChange={onUploadingChange}
                />
              ) : doc.document_type_key === "invoices" ? (
                <MarketplaceStatementsUploadForm
                  doc={doc}
                  onChange={onChange}
                  reloadDocumentsList={reloadDocumentsList}
                  documentsList={documentsList}
                  onUploadingChange={onUploadingChange}
                />
              ) : doc.document_type_key === "audited_financials" ? (
                <FinancialStatementsUploadForm
                  doc={doc}
                  documentsList={documentsList}
                  reloadDocumentsList={reloadDocumentsList}
                  required={docRequired(doc)}
                  validationError={errors?.[doc.document_type_key]}
                  onUploadingChange={onUploadingChange}
                />
              ) : doc.document_type_key === "buyer_invoices" ? (
                <BuyerInvoicesUploadForm
                  doc={doc}
                  documentsList={documentsList}
                  reloadDocumentsList={reloadDocumentsList}
                  required={docRequired(doc)}
                  validationError={errors?.[doc.document_type_key]}
                  onUploadingChange={onUploadingChange}
                />
              ) : (
                <div className="mb-4 border rounded p-3">
                  <h6>
                    {doc.title}{" "}
                    {docRequired(doc) && <span className="text-danger">*</span>}
                  </h6>
                  {doc.description && (
                    <p className="text-muted">{doc.description}</p>
                  )}
                  {errors?.[doc.document_type_key] && (
                    <p className="text-danger mb-2" role="alert">{errors[doc.document_type_key]}</p>
                  )}
                  <Form.Group controlId={`doc-upload-${doc.document_type_key}`}>
                    <Form.Control
                      type="file"
                      aria-label={`Upload ${doc.title}`}
                      isInvalid={!!errors?.[doc.document_type_key]}
                      className="file-input--hide-chosen-text"
                      name={doc.document_type_key}
                      multiple={doc.multi_upload}
                      ref={(el) => (inputRefs.current[doc.document_type] = el)}
                      onChange={(e) => handleFileChange(doc.document_type, e)}
                      disabled={uploading}
                      data-testid={
                        doc.document_type_key === "trade_license"
                          ? "fincobox-kyc-trade-license-file"
                          : undefined
                      }
                    />
                  </Form.Group>

                  <div className="mt-2">
                    <DocumentFiles
                      files={(documentsList || [])
                        .filter((d) => d.document_type === doc.document_type)
                        .flatMap((d) => d.document_files || [])}
                      reloadDocumentsList={reloadDocumentsList}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default DocumentUploadForm;
